import { ReportViewModel } from 'types/report';
import { generateCacheKey } from 'utils/cacheUtils';

// Cache constants
const AI_EDITOR_CACHE_PREFIX = 'ai-editor';
const AI_ANALYSIS_CACHE_PREFIX = 'ai-analysis-summary';
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 1 day for editor cache

export interface AIEditorCacheEntry {
  prompt: string;
  response: string;
  timestamp: number;
  responseType: 'external' | 'internal';
  model?: string;
  provider?: string;
  iteration?: number;
  title?: string;
  executionTime?: number;
}

export interface AIEditorCacheGroup {
  reportId: string;
  urlParamsString: string;
  entries: AIEditorCacheEntry[];
  lastModified: number;
}

// Reuse the same URL parameter analysis from AIAnalysisContext
const getRelevantUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const relevantParams = [
    'type',
    'difference',
    'budget',
    'customDateRange',
    'breakdownPeriods',
    'timeframe',
    'comparisonType',
    'comparisonPeriods',
    'compareEnabled',
    'dateRange',
    'total',
  ];

  return relevantParams.map(param => `${param}=${urlParams.get(param) || ''}`).join('-');
};

// Generate cache key using hashed format (only group key, no individual entries)
const generateCacheKeys = (reportData: ReportViewModel | null | undefined) => {
  if (!reportData) return { groupKey: '' };

  const { reportId } = reportData;
  const urlParamsString = getRelevantUrlParams();
  const uniqueId = `${reportId || 'no-report-id'}-${urlParamsString}`;

  // Use hashed key for consistency with AI Analysis cache
  return {
    groupKey: generateCacheKey(`${AI_EDITOR_CACHE_PREFIX}-responses`, uniqueId),
  };
};

// Generate AIAnalysis cache key (hashed, aligned with AIAnalysisContext)
const generateAIAnalysisCacheKey = (reportData: ReportViewModel | null | undefined) => {
  if (!reportData) return '';

  const { reportId } = reportData;
  const urlParamsString = getRelevantUrlParams();
  const uniqueId = `${reportId || ''}-${urlParamsString}`;

  return generateCacheKey(`${AI_ANALYSIS_CACHE_PREFIX}-data`, uniqueId);
};

export class AIEditorCacheService {
  // Check if there's existing AIAnalysis cache data that can be used as initial content
  static getInitialContentFromAIAnalysis(reportData: ReportViewModel | null | undefined): string | null {
    const dataKey = generateAIAnalysisCacheKey(reportData);
    if (!dataKey) return null;

    const tryRead = (key: string): string | null => {
      try {
        const cachedData = localStorage.getItem(key);
        if (!cachedData) return null;

        // Parse payload; support new format { timestamp, data } and legacy direct data
        const parsed = JSON.parse(cachedData);
        const embeddedTs =
          parsed && typeof parsed === 'object' && typeof parsed.timestamp === 'number' ? parsed.timestamp : null;
        const actualData = parsed && typeof parsed === 'object' && parsed.data ? parsed.data : parsed;

        // Require embedded timestamp in the new simplified format
        const ts: number | null = embeddedTs;
        if (!ts) return null;

        const now = Date.now();
        // 2-day expiry aligned with AIAnalysisContext
        if (now - ts >= 2 * 24 * 60 * 60 * 1000) return null;

        if (actualData?.report?.conceptBlocks?.length > 0) {
          const content = actualData.report.conceptBlocks
            .map((block: any) => {
              let blockContent = '';
              if (block.title) blockContent += `# ${block.title}\n\n`;
              if (block.content) blockContent += `${block.content}\n\n`;
              if (block.items?.length > 0) {
                blockContent += block.items.map((item: any) => `- ${item.text || item}`).join('\n') + '\n\n';
              }
              return blockContent;
            })
            .join('');
          return content.trim();
        }
        return null;
      } catch (e) {
        return null;
      }
    };

    return tryRead(dataKey);
  }

  // Save a response to cache
  static saveResponse(
    reportData: ReportViewModel | null | undefined,
    prompt: string,
    response: string,
    responseType: 'external' | 'internal',
    model?: string,
    provider?: string,
    title?: string,
    executionTime?: number
  ): void {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return;

    try {
      const entry: AIEditorCacheEntry = {
        prompt,
        response,
        timestamp: Date.now(),
        responseType,
        model,
        provider,
        title,
        executionTime,
      };

      // Only save to group (no individual entry needed)
      this.updateGroupIndex(groupKey, reportData, entry);
    } catch (error) {
      console.error('Failed to save AIEditor response to cache:', error);
    }
  }

  // Get the latest response of a specific type from cache
  static getResponse(
    reportData: ReportViewModel | null | undefined,
    responseType: 'external' | 'internal'
  ): AIEditorCacheEntry | null {
    const group = this.getResponseGroup(reportData);
    if (!group) return null;

    // Get the latest entry of the requested type
    const entries = group.entries.filter(e => e.responseType === responseType);
    if (entries.length === 0) return null;

    // Return the most recent one
    return entries[entries.length - 1];
  }

  // Get all responses for a report group
  static getResponseGroup(reportData: ReportViewModel | null | undefined): AIEditorCacheGroup | null {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return null;

    try {
      const cachedGroup = localStorage.getItem(groupKey);
      if (!cachedGroup) return null;

      const group: AIEditorCacheGroup = JSON.parse(cachedGroup);

      // Filter out expired entries
      const validEntries = group.entries.filter(entry => Date.now() - entry.timestamp < CACHE_EXPIRY_TIME);

      if (validEntries.length !== group.entries.length) {
        // Update group with only valid entries
        group.entries = validEntries;
        group.lastModified = Date.now();
        localStorage.setItem(groupKey, JSON.stringify(group));
      }

      return validEntries.length > 0 ? group : null;
    } catch (error) {
      console.error('Failed to get AIEditor response group from cache:', error);
      return null;
    }
  }

  // Update group index
  private static updateGroupIndex(
    groupKey: string,
    reportData: ReportViewModel | null | undefined,
    newEntry: AIEditorCacheEntry
  ): void {
    try {
      let group: AIEditorCacheGroup;
      const existingGroup = localStorage.getItem(groupKey);

      if (existingGroup) {
        group = JSON.parse(existingGroup);
        // Keep all existing entries - we want to preserve multiple runs
        // No filtering needed, just add the new entry
      } else {
        group = {
          reportId: reportData?.reportId || '',
          urlParamsString: getRelevantUrlParams(),
          entries: [],
          lastModified: Date.now(),
        };
      }

      group.entries.push(newEntry);
      group.lastModified = Date.now();

      localStorage.setItem(groupKey, JSON.stringify(group));
    } catch (error) {
      console.error('Failed to update group index:', error);
    }
  }

  // Clear all AIEditor cache
  static clearAllCache(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(AI_EDITOR_CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear AIEditor cache:', error);
    }
  }

  // Delete a specific response from cache by timestamp
  static deleteSpecificResponse(reportData: ReportViewModel | null | undefined, timestamp: number): void {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return;

    try {
      // Update group index to remove the specific entry by timestamp
      const existingGroup = localStorage.getItem(groupKey);
      if (existingGroup) {
        const group: AIEditorCacheGroup = JSON.parse(existingGroup);
        const originalLength = group.entries.length;
        group.entries = group.entries.filter(entry => entry.timestamp !== timestamp);

        if (group.entries.length !== originalLength) {
          group.lastModified = Date.now();

          if (group.entries.length > 0) {
            localStorage.setItem(groupKey, JSON.stringify(group));
          } else {
            // Remove group if no entries left
            localStorage.removeItem(groupKey);
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete specific response from cache:', error);
    }
  }

  // Delete a specific response from cache (legacy method for backward compatibility)
  static deleteResponse(reportData: ReportViewModel | null | undefined, responseType: 'external' | 'internal'): void {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return;

    try {
      // Update group index to remove entries of this type
      const existingGroup = localStorage.getItem(groupKey);
      if (existingGroup) {
        const group: AIEditorCacheGroup = JSON.parse(existingGroup);
        group.entries = group.entries.filter(entry => entry.responseType !== responseType);
        group.lastModified = Date.now();

        if (group.entries.length > 0) {
          localStorage.setItem(groupKey, JSON.stringify(group));
        } else {
          // Remove group if no entries left
          localStorage.removeItem(groupKey);
        }
      }
    } catch (error) {
      console.error('Failed to delete response from cache:', error);
    }
  }

  // Clear cache for specific report
  static clearCache(reportData: ReportViewModel | null | undefined): void {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return;

    try {
      localStorage.removeItem(groupKey);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  // Purge all cached responses but keep prompts intact
  static purgeAllResponses(reportData: ReportViewModel | null | undefined): void {
    const { groupKey } = generateCacheKeys(reportData);
    if (!groupKey) return;

    try {
      // Simply remove the entire group - this clears all responses but leaves prompts untouched
      localStorage.removeItem(groupKey);
    } catch (error) {
      console.error('Failed to purge all responses:', error);
    }
  }

  // Clear cache for specific report
  static clearReportCache(reportData: ReportViewModel | null | undefined): void {
    if (!reportData) return;

    try {
      const { groupKey } = generateCacheKeys(reportData);
      if (!groupKey) return;

      // Remove group (contains all entries)
      localStorage.removeItem(groupKey);
    } catch (error) {
      console.error('Failed to clear report cache:', error);
    }
  }

  // Get cache statistics
  static getCacheStats(): { totalEntries: number; totalSize: number } {
    let totalEntries = 0;
    let totalSize = 0;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(AI_EDITOR_CACHE_PREFIX)) {
          totalEntries++;
          totalSize += localStorage.getItem(key)?.length || 0;
        }
      });
    } catch (error) {
      console.error('Failed to get cache stats:', error);
    }

    return { totalEntries, totalSize };
  }
}
